import {ChangeDetectionStrategy, Component, computed, input, signal} from '@angular/core';
import {CommonModule} from '@angular/common';

export type V2ThumbnailSize = 'sm' | 'md' | 'lg';

/**
 * Design 2.0 vehicle thumbnail — rounded image with two-stage fallback:
 *  1. `src` (the photo URL)
 *  2. `assets/images/brands/img_<brand-slug>.png` — auto-derived from `brandName`
 *  3. Generic car icon
 *
 * Figma reference: leftmost column of `AB/VehicleList` rows; 64x48, 8px radius.
 * Sizes: sm = 48x36, md = 64x48 (default), lg = 96x72
 */
@Component({
  selector: 'app-v2-vehicle-thumbnail',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './v2-vehicle-thumbnail.component.html',
  styleUrls: ['./v2-vehicle-thumbnail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class V2VehicleThumbnailComponent {
  readonly src = input<string | null>(null);
  readonly alt = input<string>('');
  readonly size = input<V2ThumbnailSize>('md');
  /** Vehicle brand — used to look up an `assets/images/brands/img_<slug>.png` fallback. */
  readonly brandName = input<string | null>(null);

  readonly primaryImageFailed = signal(false);
  readonly brandImageFailed = signal(false);

  readonly sizeClasses = computed(() => {
    switch (this.size()) {
      case 'sm':
        return 'w-12 h-9';
      case 'lg':
        return 'w-24 h-18';
      default:
        return 'w-16 h-12';
    }
  });

  readonly showPrimary = computed(() => !!this.src() && !this.primaryImageFailed());

  readonly brandSlug = computed<string | null>(() => {
    const name = this.brandName();
    if (!name) {
      return null;
    }
    return name
      .toString()
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^\w\-]+/g, '')
      .replace(/\-\-+/g, '-')
      .replace(/^-+|-+$/g, '');
  });

  readonly brandImageUrl = computed<string | null>(() => {
    const slug = this.brandSlug();
    return slug ? `assets/images/brands/img_${slug}.png` : null;
  });

  readonly showBrandImage = computed(
    () => !this.showPrimary() && !!this.brandImageUrl() && !this.brandImageFailed(),
  );

  readonly showGenericFallback = computed(
    () => !this.showPrimary() && !this.showBrandImage(),
  );

  onPrimaryError(): void {
    this.primaryImageFailed.set(true);
  }

  onBrandError(): void {
    this.brandImageFailed.set(true);
  }
}